<div class="model-overview">
  <!-- Hero -->
  <header class="hero">
    <p class="hero-eyebrow">AI Models</p>
    <h1 class="hero-title">Compare the {{ siteName }} AI Models</h1>
    <p class="hero-subtitle">
      From hyper-realistic portraits to expressive anime art — compare every AI
      image and video model we support, see real example results, and find the
      perfect style for your next creation.
    </p>
    <div class="hero-actions">
      <a mat-flat-button color="primary" routerLink="/create">
        <mat-icon>auto_awesome</mat-icon>
        Start Creating
      </a>
      <a mat-stroked-button routerLink="/posts">Browse Gallery</a>
    </div>
  </header>

  <!-- Quick navigation -->
  <nav class="model-nav" aria-label="Available AI models">
    @for (showcase of showcases; track showcase.slug) {
      <a
        class="model-nav-chip"
        [href]="'#' + showcase.slug"
        (click)="scrollTo($event, showcase.slug)"
        mat-ripple
      >
        <mat-icon>{{ showcase.option.icon }}</mat-icon>
        <span>{{ showcase.option.name }}</span>
      </a>
    }
  </nav>

  <!-- Model sections -->
  <div class="models" id="models">
    @for (showcase of showcases; track showcase.slug) {
      <article class="model-section" [id]="showcase.slug">
        <div class="model-header">
          <div class="model-heading">
            <mat-icon class="model-icon">{{ showcase.option.icon }}</mat-icon>
            <div class="model-heading-text">
              <div class="model-title-row">
                <h2 class="model-name">{{ showcase.option.name }}</h2>
                @if (showcase.option.uncensored) {
                  <span class="model-chip uncensored">Uncensored</span>
                }
                @if (showcase.option.isNew) {
                  <app-new-badge />
                }
              </div>
              <p class="model-tagline">{{ showcase.tagline }}</p>
            </div>
          </div>
          <a
            mat-flat-button
            color="primary"
            [routerLink]="['/create', showcase.route]"
            class="model-cta"
          >
            Try {{ showcase.option.name }}
          </a>
        </div>

        <p class="model-description">{{ showcase.longDescription }}</p>

        @if (showcase.bestFor.length) {
          <ul class="model-tags" aria-label="Best for">
            @for (tag of showcase.bestFor; track tag) {
              <li class="model-tag">{{ tag }}</li>
            }
          </ul>
        }

        <div class="model-body">
          <!-- Example gallery -->
          <section class="model-gallery" aria-label="Example images">
            @let items = galleryByModel()[showcase.slug] ?? [];
            @for (item of items; track item.shareId) {
              <a
                class="gallery-item"
                [routerLink]="['/share', item.shareId]"
                [attr.aria-label]="
                  'View this ' + showcase.option.name + ' example'
                "
              >
                @if (item.media.mediaType === MediaType.Video) {
                  <video
                    [src]="item.media.mediaUrl"
                    muted
                    loop
                    autoplay
                    playsinline
                    preload="metadata"
                  ></video>
                } @else {
                  <img
                    [src]="item.media.mediaUrl"
                    [alt]="showcase.option.name + ' AI model example image'"
                    loading="lazy"
                  />
                }
                <span class="gallery-generate">
                  <mat-icon>auto_awesome</mat-icon>
                  <span>Generate this</span>
                </span>
              </a>
            } @empty {
              @for (placeholder of [0, 1, 2, 3, 4, 5]; track placeholder) {
                <div
                  class="gallery-item gallery-skeleton"
                  aria-hidden="true"
                ></div>
              }
            }
          </section>

          <!-- Highlights -->
          @if (showcase.highlights.length) {
            <aside class="model-highlights">
              <h3 class="highlights-title">Highlights</h3>
              <ul>
                @for (highlight of showcase.highlights; track highlight) {
                  <li>
                    <mat-icon>check_circle</mat-icon>
                    <span>{{ highlight }}</span>
                  </li>
                }
              </ul>
            </aside>
          }
        </div>
      </article>
    }
  </div>

  <!-- FAQ -->
  <section class="faq" aria-label="Frequently asked questions">
    <h2 class="faq-title">Frequently Asked Questions</h2>
    <div class="faq-list">
      @for (item of faq; track item.question) {
        <div class="faq-item">
          <h3 class="faq-question">{{ item.question }}</h3>
          <p class="faq-answer">{{ item.answer }}</p>
        </div>
      }
    </div>
  </section>

  <!-- Final CTA -->
  <section class="final-cta">
    <h2 class="final-cta-title">Ready to bring your ideas to life?</h2>
    <p class="final-cta-subtitle">
      Pick a model, write a prompt, and start generating in seconds.
    </p>
    <a
      mat-flat-button
      color="primary"
      routerLink="/create"
      class="final-cta-btn"
    >
      <mat-icon>auto_awesome</mat-icon>
      Create Now — It's Free
    </a>
  </section>
</div>
